/**
 * Admin Price Scheduler — the daily auto price-increase tool.
 *
 *   - Toggle the escalator on/off and set a daily percentage.
 *   - The backend cron compounds that % onto every product price at 02:00
 *     Pacific time each day until turned off.
 *   - Starting prices are snapshotted as a baseline when enabled, so "Reset to
 *     baseline" fully undoes every increase.
 *   - "Run today now" applies one day's increase immediately (manual / preview).
 */
import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/helpers';
import { useCurrency } from '../../hooks/useCurrency';
import Icon from './Icon';

export default function PriceSchedulerPage() {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData]       = useState(null);
  const [percent, setPercent] = useState('2');
  const [busy, setBusy]       = useState(false);

  const apply = (d) => {
    setData(d);
    setPercent(String(d.percent ?? 0));
  };

  const load = () => {
    api.get('/admin/pricing')
      .then(res => apply(res.data))
      .catch(() => toast.error('Failed to load price scheduler'));
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      const { data: d } = await api.patch('/admin/pricing', body);
      apply(d);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  const saveRate = () => {
    const p = parseFloat(percent);
    if (isNaN(p) || p < 0 || p > 100) { toast.error('Enter a percentage between 0 and 100'); return; }
    patch({ percent: p }, 'Daily rate saved');
  };

  const toggle = () => {
    if (!data.enabled) {
      const p = parseFloat(percent);
      if (isNaN(p) || p <= 0) { toast.error('Set a daily percentage above 0 before enabling'); return; }
      patch({ enabled: true, percent: p }, 'Auto price increase ON');
    } else {
      patch({ enabled: false }, 'Auto price increase paused');
    }
  };

  const runNow = async () => {
    if (!window.confirm(`Apply a ${percent}% increase to every product right now? This counts as today's run.`)) return;
    setBusy(true);
    try {
      const { data: d } = await api.post('/admin/pricing/run-now');
      apply(d);
      toast.success(d.result?.ran ? `Raised ${d.result.updated} prices` : 'Nothing to run (escalator is off)');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Run failed');
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm('Restore ALL product prices to their starting baseline and stop the escalator? This undoes every increase.')) return;
    setBusy(true);
    try {
      const { data: d } = await api.post('/admin/pricing/reset-baseline');
      apply(d);
      toast.success('Prices reset to baseline');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-gray-400 py-10 text-center">Loading…</div>;

  const raisedSoFar = data.currentTotalCents - data.baselineTotalCents;

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Icon name="trend" className="h-6 w-6 text-tea-600" /> Price Scheduler</h2>
        <p className="text-sm text-gray-500 mt-1">
          Automatically raise every product price by a set percentage each day, until you stop it.
        </p>
      </div>

      {/* Status banner */}
      <div className={'rounded-xl border p-4 mb-5 flex items-center justify-between flex-wrap gap-3 ' +
        (data.enabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200')}>
        <div>
          <div className="text-sm font-semibold">
            {data.enabled
              ? <span className="text-blue-700">● Active — prices rise {data.percent}% every day</span>
              : <span className="text-gray-500">○ Paused — prices are not changing automatically</span>}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Runs nightly at 2:00 AM Pacific time.
            {data.lastRunDate ? ` Last run: ${formatDate(data.lastRunDate)}.` : ' Has not run yet.'}
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
          className={'text-sm font-bold px-4 py-2 rounded-lg text-white disabled:opacity-50 ' +
            (data.enabled ? 'bg-gray-600 hover:bg-gray-700' : 'bg-tea-600 hover:bg-tea-700')}>
          {data.enabled ? 'Pause' : 'Turn on'}
        </button>
      </div>

      {/* Rate setting */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Daily increase (%)</label>
        <div className="flex gap-2 items-center">
          <input type="number" min="0" max="100" step="0.01" value={percent}
            onChange={e => setPercent(e.target.value)}
            className="w-32 text-sm border border-gray-300 rounded-lg px-3 py-2" />
          <span className="text-sm text-gray-500">% per day (compounds)</span>
          <button onClick={saveRate} disabled={busy}
            className="ml-auto text-sm font-semibold px-4 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 disabled:opacity-50">
            Save rate
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Compounds on the current price each day — e.g. 2%/day is roughly +46% over 30 days.
        </p>
      </div>

      {/* Live stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Products tracked" value={data.variantCount} />
        <Stat label="Baseline total" value={fmt(data.baselineTotalCents)} />
        <Stat label="Current total" value={fmt(data.currentTotalCents)} />
        <Stat label="Next run adds ≈" value={fmt(data.projectedNextDeltaCents)} />
      </div>

      {raisedSoFar > 0 && (
        <div className="text-sm text-gray-600 mb-5">
          Raised <span className="font-semibold text-blue-700">{fmt(raisedSoFar)}</span> above baseline across all products so far.
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={runNow} disabled={busy}
          className="text-sm font-bold px-4 py-2 rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
          Run today now
        </button>
        <button onClick={reset} disabled={busy || data.baselineTotalCents === data.currentTotalCents}
          className="text-sm font-semibold px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
          Reset to baseline
        </button>
      </div>

      <p className="text-[11px] text-gray-400 mt-4 max-w-prose">
        Starting prices are snapshotted the moment you turn the escalator on. "Reset to baseline" restores every
        product to those starting prices and pauses the schedule. Only the regular price changes — sale/compare-at
        and wholesale prices are left untouched.
      </p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl p-3 text-center bg-white border shadow-sm">
      <div className="text-lg font-bold text-gray-800 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}
